import { useCallback } from "react";
import { atom } from "jotai";
import { useSetAtom } from "jotai";
import type { ReactFlowInstance, XYPosition } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import { isEqual } from "lodash";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import type { NodeDefinition } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import {
  nodesAtom,
  nodeRegistryAtom,
  isEditorReadOnlyAtom,
  workflowTypeAtom,
} from "../atoms";
import { onlyMaterializesDefaults } from "../materializedDefaults";
import {
  createStickyNoteReactFlowNode,
  findNodeDisplayName,
  getInputHandles,
  getOutputHandles,
  getRegistryConfigDefaults,
  getRegistryPinnedFields,
  isStickyNoteDefinitionType,
  isStickyNoteReactFlowNode,
  markDirty,
  nextFlowIODefaultName,
  uniqueFlowIOName,
} from "../helpers";
import {
  DESCRIPTION_PIN_KEY,
  isDescriptionPinnedToCard,
  withDescriptionCardPinned,
  withDescriptionCardUnpinned,
} from "@/components/SpotflowV2/descriptionCard";
import {
  isFlowInputNode,
  isFlowOutputNode,
} from "@/components/SpotflowV2/constants";
import {
  DEFAULT_NODE_HEIGHT,
  DEFAULT_NODE_WIDTH,
} from "@/components/Spotflow/Editor/EditorUtils";
import {
  recordTransaction,
  withoutHistory,
  type TransactionDrafts,
} from "../history/recordTransaction";
import {
  absoluteNodePosition,
  expandGroupFrameToContain,
  groupContainingPoint,
  groupFrame,
  isGroupReactFlowNode,
  nodeSize,
  reframeGroupInPlace,
  type GroupFrame,
} from "../groups";

/** Compute a stable collapse key for nodeConfig edits, scoped to top-level field. */
function configCollapseKey(
  nodeId: string,
  oldConfig: Record<string, unknown> | undefined,
  newConfig: Record<string, unknown>
): string {
  // Find the first top-level key that actually changed. If multiple changed
  // (rare, only on programmatic resets), fall back to a generic key so the
  // entry won't merge with single-field bursts.
  const changed: string[] = [];
  const keys = new Set([
    ...Object.keys(oldConfig ?? {}),
    ...Object.keys(newConfig),
  ]);
  for (const k of keys) {
    if (oldConfig?.[k] !== newConfig[k]) changed.push(k);
  }
  const field = changed.length === 1 ? changed[0] : "*";
  return `config:${nodeId}:${field}`;
}

/**
 * Per-field equality check with a reference fast-path. Most config edits
 * change a single field; the other N-1 fields keep reference identity from
 * the previous form snapshot, so this short-circuits in O(N) ===-comparisons
 * instead of walking the full structure with `isEqual`. Falls back to
 * `isEqual` only on a reference miss to handle the changed field correctly
 * even when its inner shape happens to be structurally equal (e.g., the
 * form re-instantiates an array with the same contents).
 */
function configsEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (a[k] === b[k]) continue;
    if (!isEqual(a[k], b[k])) return false;
  }
  return true;
}

// ── Add node ──────────────────────────────────────────────────────────────

export const addNodeAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeType: string;
      title: string;
      color: string;
      image?: string;
      deletable?: boolean;
      /** Only one node of this type may exist in the flow. */
      unique?: boolean;
      /** Flow types the node may be added to; undefined = any. */
      allowedInFlowTypes?: string[];
      outputHandles: string[];
      inputHandles?: string[];
      position?: XYPosition;
      id?: string;
      defaultNodeConfig?: Record<string, unknown>;
      defaultPinnedFields?: string[];
      /** Group the gesture targeted (edge dropped inside a group, picker
       *  opened from a node that lives in one). Wins over the position-based
       *  group detection. */
      parentGroupId?: string;
    }
  ) => {
    if (get(isEditorReadOnlyAtom)) return undefined;
    const {
      nodeType,
      title,
      color,
      image,
      deletable = true,
      unique = false,
      allowedInFlowTypes,
      outputHandles,
      inputHandles = [],
      position,
      id,
      defaultNodeConfig,
      defaultPinnedFields,
      parentGroupId,
    } = args;

    const existingNodes = get(nodesAtom);

    const workflowType = get(workflowTypeAtom);
    if (
      workflowType &&
      allowedInFlowTypes &&
      !allowedInFlowTypes.includes(workflowType)
    ) {
      SpotToast.error(`${title} node is not allowed in this workflow type`);
      return undefined;
    }

    if (unique && existingNodes.some((n) => n.data.nodeType === nodeType)) {
      SpotToast.error(`${title} node already exists in this workflow`);
      return undefined;
    }
    // Seed the node with the registry's `Parameter(default=...)` values so
    // fields show their defaults immediately (bindable fields read straight
    // from nodeConfig). Without this the schema defaults were discarded.
    let nodeConfig: Record<string, unknown> = { ...(defaultNodeConfig ?? {}) };

    let name = title;
    // Plugin nodes are all the generic PluginNodeV2 type (title "Plugin Node"),
    // so name from the picker-seeded `plugin_display_name` — the specific node's
    // title ("E2E Echo (V2)") — instead of the generic type title.
    const pluginDisplayName = nodeConfig.plugin_display_name;
    if (typeof pluginDisplayName === "string" && pluginDisplayName.trim()) {
      name = pluginDisplayName;
    }
    if (isFlowInputNode(nodeType) || isFlowOutputNode(nodeType)) {
      name = nextFlowIODefaultName(existingNodes, nodeType);
      nodeConfig = { ...nodeConfig, label: name };
    }

    const initialPosition = position ?? {
      x: 400 + Math.floor(Math.random() * 120),
      y: 300 + Math.floor(Math.random() * 120),
    };
    const childSize = {
      width: DEFAULT_NODE_WIDTH,
      height: DEFAULT_NODE_HEIGHT,
    };

    // Inline parent detection — assigning parentId in the SAME atom write
    // that creates the node guarantees it can't be lost to a race with
    // autosave or applyNodeChanges. Notes never nest.
    let parentId: string | undefined;
    let positionInParent = initialPosition;
    let groupReframe: { groupId: string; frame: GroupFrame } | null = null;
    if (!isStickyNoteDefinitionType(nodeType)) {
      const requestedGroup = existingNodes.find(
        (n) => n.id === parentGroupId && isGroupReactFlowNode(n)
      );
      const group =
        requestedGroup ?? groupContainingPoint(existingNodes, initialPosition);
      if (group) {
        // Grow the frame around the new node's footprint up front; React Flow
        // clamps `extent: "parent"` children into the frame, so a node added
        // past its edge would otherwise render pushed back inside.
        const current = groupFrame(group);
        const frame =
          expandGroupFrameToContain(current, {
            ...initialPosition,
            ...childSize,
          }) ?? current;
        parentId = group.id;
        positionInParent = {
          x: initialPosition.x - frame.position.x,
          y: initialPosition.y - frame.position.y,
        };
        groupReframe = { groupId: group.id, frame };
      }
    }

    const newNode: NoxusReactflowNodeV2 = {
      id: id ?? uuidv4(),
      type: "baseNodeV2",
      position: positionInParent,
      // Group members are pinned inside their frame (V1 group semantics).
      ...(parentId ? { parentId, extent: "parent" as const } : {}),
      deletable,
      data: {
        nodeType,
        name,
        nodeConfig,
        variableBindings: {},
        outputHandles,
        inputHandles,
        pinnedFields: defaultPinnedFields ?? [],
        iterate: false,
        iterateOverrides: {},
        title,
        color,
        image,
        deletable,
      },
    };

    // React Flow requires the parent to come before its children — appending
    // newNode at the END of the array preserves that invariant.
    recordTransaction(get, set, `Add ${title || nodeType}`, ({ nodes }) => {
      if (groupReframe) {
        reframeGroupInPlace(nodes, groupReframe.groupId, groupReframe.frame);
      }
      nodes.push(newNode as unknown as (typeof nodes)[number]);
    });
    markDirty(set);

    return newNode.id;
  }
);

// ── Add sticky note ───────────────────────────────────────────────────────

export const addStickyNoteV2Atom = atom(
  null,
  (get, set, position?: XYPosition) => {
    if (get(isEditorReadOnlyAtom)) return undefined;
    const newNode = createStickyNoteReactFlowNode({
      position: position ?? {
        x: 400 + Math.floor(Math.random() * 120),
        y: 300 + Math.floor(Math.random() * 120),
      },
    });

    recordTransaction(get, set, "Add sticky note", ({ nodes }) => {
      nodes.push(newNode as unknown as (typeof nodes)[number]);
    });
    markDirty(set);
    return newNode.id;
  }
);

export const duplicateStickyNoteV2Atom = atom(
  null,
  (
    get,
    set,
    args: { sourceId: string; nodeConfig: Record<string, unknown> }
  ) => {
    if (get(isEditorReadOnlyAtom)) return;
    const source = get(nodesAtom).find((n) => n.id === args.sourceId);
    if (!source || !isStickyNoteReactFlowNode(source)) return;

    const newNode = createStickyNoteReactFlowNode({
      position: {
        x: source.position.x + 30,
        y: source.position.y + 30,
      },
      nodeConfig: args.nodeConfig,
      size: {
        width: source.width,
        height: source.height,
      },
    });

    recordTransaction(get, set, "Duplicate note", ({ nodes }) => {
      nodes.push(newNode as unknown as (typeof nodes)[number]);
    });
    markDirty(set);
  }
);

// ── Update node config ────────────────────────────────────────────────────

export const updateNodeConfigAtom = atom(
  null,
  (get, set, args: { nodeId: string; nodeConfig: Record<string, unknown> }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { nodeId, nodeConfig } = args;
    const current = get(nodesAtom);
    const existing = current.find((n) => n.id === nodeId);
    const existingConfig = existing?.data.nodeConfig as
      | Record<string, unknown>
      | undefined;

    // Bail on no-op writes. Without this, post-undo form re-syncs (the panel
    // calls form.reset which fires its watcher with the same values) would
    // record a redundant transaction, clearing the redo stack and trapping
    // the user. `configsEqual` short-circuits on reference equality per key,
    // which is dramatically cheaper than `isEqual` over the full config when
    // only one field actually changed.
    if (existingConfig && configsEqual(existingConfig, nodeConfig)) return;

    const collapseKey = configCollapseKey(nodeId, existingConfig, nodeConfig);
    const displayName = findNodeDisplayName(current, nodeId);
    const mutate = ({ nodes }: TransactionDrafts) => {
      for (const n of nodes) {
        if (n.id !== nodeId) continue;
        // Per-key writes (instead of replacing the whole `nodeConfig`)
        // produce per-field immer patches. Undo entries then carry only the
        // diff for the changed key, not a full snapshot of the entire
        // config — meaningful for sticky notes / long-prompt fields where
        // the config blob can be tens of KB.
        const cfg = (n.data as { nodeConfig: Record<string, unknown> })
          .nodeConfig;
        for (const k of Object.keys(cfg)) {
          if (!(k in nodeConfig)) delete cfg[k];
        }
        for (const k of Object.keys(nodeConfig)) {
          if (cfg[k] === nodeConfig[k]) continue;
          if (isEqual(cfg[k], nodeConfig[k])) continue;
          cfg[k] = nodeConfig[k];
        }
        break;
      }
    };
    const registry = get(nodeRegistryAtom).find(
      (r) => r.type === existing?.data.nodeType
    );
    if (
      registry &&
      onlyMaterializesDefaults(
        existingConfig ?? {},
        nodeConfig,
        registry.config
      )
    ) {
      withoutHistory(get, set, () =>
        recordTransaction(get, set, `Edit ${displayName}`, mutate)
      );
      return;
    }
    recordTransaction(get, set, `Edit ${displayName}`, mutate, { collapseKey });
    markDirty(set);
  }
);

// ── Update variable bindings ──────────────────────────────────────────────

export const updateVariableBindingsAtom = atom(
  null,
  (
    get,
    set,
    args: { nodeId: string; variableBindings: Record<string, string> }
  ) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { nodeId, variableBindings } = args;
    const displayName = findNodeDisplayName(get(nodesAtom), nodeId);
    recordTransaction(
      get,
      set,
      `Bind variable on ${displayName}`,
      ({ nodes }) => {
        for (const n of nodes) {
          if (n.id === nodeId) {
            (
              n.data as { variableBindings: Record<string, string> }
            ).variableBindings = variableBindings;
            break;
          }
        }
      }
    );
    markDirty(set);
  }
);

// ── Add new node from picker (needs ReactFlowInstance for screen→flow) ────

export function useAddNewNode(rfInstance: ReactFlowInstance | null) {
  const addNode = useSetAtom(addNodeAtom);
  return useCallback(
    (
      nodeType: string,
      registryNode: NodeDefinition | undefined,
      position?: { x: number; y: number },
      id?: string,
      titleOverride?: string,
      parentGroupId?: string
    ): string | undefined => {
      if (!registryNode) {
        SpotToast.error(`Unknown node type: ${nodeType}`);
        return undefined;
      }

      const flowPosition = position
        ? (rfInstance?.screenToFlowPosition(position) ?? position)
        : undefined;

      const nodeId = id ?? uuidv4();
      const createdId = addNode({
        id: nodeId,
        nodeType,
        title: titleOverride ?? registryNode.title ?? nodeType,
        color:
          (registryNode as unknown as { color?: string })?.color ?? "#6366f1",
        image: registryNode.image ?? undefined,
        deletable: registryNode.deletable ?? true,
        unique: registryNode.unique ?? false,
        allowedInFlowTypes: registryNode.allowed_in_flow_types,
        outputHandles: getOutputHandles(registryNode),
        inputHandles: getInputHandles(registryNode),
        position: flowPosition,
        defaultNodeConfig: getRegistryConfigDefaults(registryNode),
        defaultPinnedFields: getRegistryPinnedFields(registryNode),
        parentGroupId,
      });
      return createdId;
    },
    [addNode, rfInstance]
  );
}

// ── Pinned fields ─────────────────────────────────────────────────────────

export const togglePinnedFieldAtom = atom(
  null,
  (get, set, args: { nodeId: string; fieldKey: string }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { nodeId, fieldKey } = args;
    const currentNodes = get(nodesAtom);
    const node = currentNodes.find((n) => n.id === nodeId);
    if (!node) return;
    const currentPinned = (node.data.pinnedFields ?? []) as string[];
    const willUnpin = currentPinned.includes(fieldKey);
    const displayName = findNodeDisplayName(currentNodes, nodeId);
    const label = willUnpin
      ? `Unpin field on ${displayName}`
      : `Pin field on ${displayName}`;

    recordTransaction(get, set, label, ({ nodes }) => {
      for (const n of nodes) {
        if (n.id !== nodeId) continue;
        const data = n.data as { pinnedFields?: string[] };
        const pinned = (data.pinnedFields ?? []) as string[];
        data.pinnedFields = willUnpin
          ? pinned.filter((k) => k !== fieldKey)
          : [...pinned, fieldKey];
        break;
      }
    });
    markDirty(set);
  }
);

export const toggleDescriptionCardPinAtom = atom(
  null,
  (get, set, args: { nodeId: string }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { nodeId } = args;
    const currentNodes = get(nodesAtom);
    const node = currentNodes.find((n) => n.id === nodeId);
    if (!node) return;

    const nodeConfig = (node.data.nodeConfig ?? {}) as Record<string, unknown>;
    const willUnpin = isDescriptionPinnedToCard(nodeConfig);
    const displayName = findNodeDisplayName(currentNodes, nodeId);
    const label = willUnpin
      ? `Unpin description on ${displayName}`
      : `Pin description on ${displayName}`;

    recordTransaction(get, set, label, ({ nodes }) => {
      for (const n of nodes) {
        if (n.id !== nodeId) continue;
        const data = n.data as {
          nodeConfig: Record<string, unknown>;
          pinnedFields?: string[];
        };
        const cfg = data.nodeConfig ?? {};
        data.nodeConfig = willUnpin
          ? withDescriptionCardUnpinned(cfg)
          : withDescriptionCardPinned(cfg);
        const pinned = (data.pinnedFields ?? []) as string[];
        data.pinnedFields = willUnpin
          ? pinned.filter((k) => k !== DESCRIPTION_PIN_KEY)
          : pinned.includes(DESCRIPTION_PIN_KEY)
            ? pinned
            : [...pinned, DESCRIPTION_PIN_KEY];
        break;
      }
    });
    markDirty(set);
  }
);

export const setPinnedFieldsAtom = atom(
  null,
  (get, set, args: { nodeId: string; pinnedFields: string[] }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { nodeId, pinnedFields } = args;
    const displayName = findNodeDisplayName(get(nodesAtom), nodeId);
    recordTransaction(
      get,
      set,
      `Set pinned fields on ${displayName}`,
      ({ nodes }) => {
        for (const n of nodes) {
          if (n.id !== nodeId) continue;
          (n.data as { pinnedFields: string[] }).pinnedFields = pinnedFields;
          break;
        }
      }
    );
    markDirty(set);
  }
);

// ── Iterate / gather override ─────────────────────────────────────────────

/**
 * Set or clear the per-instance iteration override for an input field on a node.
 *   value === true  → fan out (override class default gather_field)
 *   value === false → pass whole list (override default fan-out)
 *   value === null  → clear the override, fall back to class default
 */
export const setIterateOverrideAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeId: string;
      fieldName: string;
      value: boolean | null;
    }
  ) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { nodeId, fieldName, value } = args;
    set(nodesAtom, (nodes) =>
      nodes.map((n) => {
        if (n.id !== nodeId) return n;
        const current = { ...(n.data.iterateOverrides ?? {}) };
        if (value === null) {
          delete current[fieldName];
        } else {
          current[fieldName] = value;
        }
        return { ...n, data: { ...n.data, iterateOverrides: current } };
      })
    );
    markDirty(set);
  }
);

/**
 * Apply node-level iteration mode for several nodes in one transaction.
 * Iteration is auto-derived (see useAutoIterate) and a single binding change
 * can cascade down a chain, so updates arrive batched. When on, a node runs
 * once per element of its list-bound inputs; when off, lists are passed whole.
 */
export const setIterateModesAtom = atom(
  null,
  (get, set, args: { updates: Record<string, boolean> }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { updates } = args;
    set(nodesAtom, (nodes) =>
      nodes.map((n) =>
        n.id in updates && !!n.data.iterate !== updates[n.id]
          ? { ...n, data: { ...n.data, iterate: updates[n.id] } }
          : n
      )
    );
    markDirty(set, "derived");
  }
);

// ── Rename node ───────────────────────────────────────────────────────────

export const renameNodeAtom = atom(
  null,
  (get, set, args: { nodeId: string; name: string }): string => {
    const { nodeId, name } = args;
    if (get(isEditorReadOnlyAtom)) return name;
    const nodes = get(nodesAtom);
    const previousName = findNodeDisplayName(nodes, nodeId);
    const nodeType = nodes.find((n) => n.id === nodeId)?.data.nodeType;
    // Flow I/O names must stay unique per kind; auto-suffix on collision and
    // keep the "label" config in sync with the name in the same transaction
    // (covers canvas inline rename, which has no form to drive the sync).
    const isFlowIO = isFlowInputNode(nodeType) || isFlowOutputNode(nodeType);
    const finalName = isFlowIO
      ? uniqueFlowIOName(nodes, nodeType as string, name, nodeId)
      : name;
    recordTransaction(
      get,
      set,
      `Rename ${previousName}`,
      ({ nodes }) => {
        for (const n of nodes) {
          if (n.id === nodeId) {
            (n.data as { name: string }).name = finalName;
            if (isFlowIO) {
              const cfg = (n.data.nodeConfig ?? {}) as Record<string, unknown>;
              cfg.label = finalName;
              n.data.nodeConfig = cfg;
            }
            break;
          }
        }
      },
      { collapseKey: `rename:${nodeId}` }
    );
    markDirty(set);
    return finalName;
  }
);
